import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { getCurrentYear, getInlineLoader } from "../Helpers";
import { FaArrowRightLong, FaLocationDot } from "react-icons/fa6";
import { contactItems } from "./ContactUsContent";

export default function Footer() {
  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);
  const links = [
    { label: "Home", item: "home" },
    { label: "About Us", item: "about_us" },
    { label: "Services", item: "services" },
    { label: "Certifications", item: "certifications" },
    { label: "Contact Us", item: "contact_us" },
  ];

  async function init() {
    setReady(false);
    setReady(true);
  }

  useEffect(() => {
    init();
  }, []);

  if (!ready) {
    return (
      <div style={{ display: 'flex', height: '100vh', width: '100%', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0b2635' }}>
        {getInlineLoader()}
      </div>
    );
  }

  return (
    <>
      <style>{`
        :root {
          --primaryColor: #FFC511;
          --onPrimaryColor: #fefefe;
          --secondaryColor: #0089D8;
          --onSecondaryColor: #fefefe;
          --surfaceColor: #ffffff;
          --onSurfaceColor: #0c2d41;
          --backgroundColor: #ffffff;
          --onBackgroundColor: #0b2635;
          --extraColor: #e4c970;
          --font-main: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        }

        .tech-footer {
          font-family: var(--font-main);
          background: linear-gradient(180deg, var(--onBackgroundColor) 0%, #071a26 100%);
          color: #94a3b8;
          padding: 5rem 1.5rem 2rem 1.5rem;
          position: relative;
          overflow: hidden;
        }

        /* Subtle tech pattern background */
        .tech-footer::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image: 
            radial-gradient(circle at 25% 25%, rgba(0, 137, 216, 0.06) 0%, transparent 50%),
            radial-gradient(circle at 75% 75%, rgba(255, 197, 17, 0.04) 0%, transparent 50%);
          pointer-events: none;
        }

        /* Top accent line */
        .tech-footer::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, var(--secondaryColor), transparent);
          opacity: 0.5;
        }

        .tech-footer-container {
          position: relative;
          max-width: 1200px;
          margin: 0 auto;
        }

        .tech-footer-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 3rem;
          padding-bottom: 3rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
        }
        @media (min-width: 768px) {
          .tech-footer-grid {
            grid-template-columns: 1.2fr 1fr 1fr;
            gap: 4rem;
          }
        }

        /* Brand Column */
        .tech-footer-brand .footer-kicker {
          display: block;
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          color: var(--secondaryColor);
          margin-bottom: 1.25rem;
        }
        .tech-footer-brand .footer-tagline {
          font-size: 1.5rem;
          font-weight: 700;
          color: #ffffff;
          margin: 0 0 1.25rem 0;
          letter-spacing: -0.02em;
          line-height: 1.2;
        }
        .tech-footer-brand .footer-description {
          font-size: 0.95rem;
          line-height: 1.7;
          color: #94a3b8;
          margin: 0 0 2rem 0;
          max-width: 320px;
        }
        .tech-footer-location {
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 0.9rem;
          color: #cbd5e1;
          padding: 0.5rem 0;
        }
        .tech-footer-location svg {
          color: var(--primaryColor);
        }

        /* Links Column */
        .tech-footer-column h4 {
          font-size: 0.8rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--secondaryColor);
          margin: 0 0 1.5rem 0;
        }
        .tech-footer-links {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }
        .tech-footer-link {
          background: none;
          border: none;
          padding: 0.6rem 0;
          font-size: 0.95rem;
          color: #94a3b8;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          transition: all 0.25s ease;
          text-align: left;
          position: relative;
        }
        .tech-footer-link::before {
          content: '';
          position: absolute;
          left: 0;
          width: 0;
          height: 1px;
          background: var(--secondaryColor);
          transition: width 0.25s ease;
        }
        .tech-footer-link:hover {
          color: #ffffff;
          padding-left: 0.5rem;
        }
        .tech-footer-link:hover::before {
          width: 20px;
        }
        .tech-footer-link svg {
          color: var(--secondaryColor);
          opacity: 0;
          transform: translateX(-8px);
          transition: all 0.25s ease;
        }
        .tech-footer-link:hover svg {
          opacity: 1;
          transform: translateX(0);
        }

        /* Contact Column */
        .tech-footer-contact {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .tech-footer-contact-item {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          padding: 0.75rem 0;
          transition: transform 0.2s ease;
        }
        .tech-footer-contact-item:hover {
          transform: translateX(4px);
        }
        .tech-footer-contact-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.05);
          color: var(--primaryColor);
          flex-shrink: 0;
          transition: background 0.2s ease;
        }
        .tech-footer-contact-item:hover .tech-footer-contact-icon {
          background: rgba(255, 197, 17, 0.15);
        }
        .tech-footer-contact-copy {
          display: flex;
          flex-direction: column;
          gap: 0.15rem;
        }
        .tech-footer-contact-label {
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #64748b;
        }
        .tech-footer-contact-value {
          font-size: 0.95rem;
          color: #cbd5e1;
          text-decoration: none;
          transition: color 0.2s ease;
        }
        .tech-footer-contact-value:hover {
          color: var(--secondaryColor);
        }
        .tech-footer-contact-value[href] {
          cursor: pointer;
        }

        /* Meta Footer */
        .tech-footer-meta {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          align-items: center;
          gap: 1rem;
          padding-top: 2rem;
          font-size: 0.85rem;
          color: #64748b;
        }
        .tech-footer-meta span {
          opacity: 0.9;
        }
        .tech-footer-meta span:last-child {
          color: var(--secondaryColor);
          font-weight: 500;
        }

        /* Responsive adjustments */
        @media (max-width: 767px) {
          .tech-footer-brand .footer-tagline {
            font-size: 1.25rem;
          }
          .tech-footer-grid {
            gap: 2.5rem;
          }
        }
      `}</style>

      <footer className="tech-footer ">
        <div className="tech-footer-container">
          <div className="tech-footer-grid">
            
            {/* Brand Column */}
            <div className="tech-footer-brand">
              <h3 className="text-secondary font-bold">Nova Technologies</h3>
              <h3 className="footer-tagline">Classic clarity. Modern capability.</h3>
              <p className="footer-description">
                Nova Technologies Company Limited delivers innovative ICT solutions that
                help organizations improve efficiency, strengthen reliability, and move
                forward with digital transformation.
              </p>
              <div className="tech-footer-location">
                <FaLocationDot size={36} className="bg-secondary p-2 rounded text-light" />
                <span>Makumbusho Derm Plaza,11th Floor <br /> Dar es Salaam, Tanzania</span>
              </div>
            </div>

            {/* Navigation Links */}
            <div className="tech-footer-column">
              <h4>Explore</h4>
              <div className="tech-footer-links">
                {links.map((link) => (
                  <button
                    key={link.label}
                    className="tech-footer-link"
                    onClick={() => appContext.navTo({ item: link.item })}
                  >
                    <FaArrowRightLong size={14} />
                    {link.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Contact Info */}
            <div className="tech-footer-column">
              <h4>Contact</h4>
              <div className="tech-footer-contact">
                {contactItems.map((item) =>
                  item.href ? (
                    <a
                      key={item.title}
                      href={item.href}
                      className="tech-footer-contact-item"
                      target={item.external ? "_blank" : undefined}
                      rel={item.external ? "noopener noreferrer" : undefined}
                    >
                      <span className="tech-footer-contact-icon">{item.icon}</span>
                      <span className="tech-footer-contact-copy">
                        <small className="tech-footer-contact-label">{item.title}</small>
                        <span className="tech-footer-contact-value">{item.value}</span>
                      </span>
                    </a>
                  ) : (
                    <div key={item.title} className="tech-footer-contact-item">
                      <span className="tech-footer-contact-icon">{item.icon}</span>
                      <span className="tech-footer-contact-copy">
                        <small className="tech-footer-contact-label">{item.title}</small>
                        <span className="tech-footer-contact-value">{item.value}</span>
                      </span>
                    </div>
                  )
                )}
              </div>
            </div>

          </div>
        </div>
      </footer>
    </>
  );
}